/**
 * Copyright (c) 2026, CheckPlus Limited
 */
import React, { Component } from "react";
import { simpleFetch } from "../../../restWrapper.js";
import PropTypes from "prop-types";
import UpgradeNoticeCard from "./UpgradeNoticeCard";

const defaultFormData = {
  clientId: '',
  clientSecret: '',
  signingKeyId: '',
  signingKey: '',
  paymeTransactionFeePercentage: '',
};

class RenderPayMe extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isEditMode: false,
      formData: defaultFormData,
      isSaving: false,
    };

    this.handleToggleEditMode = this.handleToggleEditMode.bind(this);
    this.handleInputChange = this.handleInputChange.bind(this);
    this.handleSave = this.handleSave.bind(this);
    this.mapFormDataFromProps = this.mapFormDataFromProps.bind(this);
    this.maskValue = this.maskValue.bind(this);
    this.getDisplayValue = this.getDisplayValue.bind(this);
  }

  componentDidMount() {
    const newFormData = this.mapFormDataFromProps();
    this.setState({ formData: newFormData });
  }

  handleToggleEditMode() {
    const { isEditMode } = this.state;

    if (isEditMode) {
      // Cancel - reset form data to saved values
      const newFormData = this.mapFormDataFromProps();
      this.setState({ formData: newFormData });
    }

    this.setState({ isEditMode: !isEditMode });
  }

  handleInputChange(e) {
    const { name, value } = e.target;
    this.setState((prevState) => ({
      formData: {
        ...prevState.formData,
        [name]: value,
      },
    }));
  }

  handleSave() {
    const { formData } = this.state;
    const { state, func } = this.props;

    this.setState({ isSaving: true });

    simpleFetch("/user/payments/addPayMe", "POST", {
      username: state.username,
      clientId: formData.clientId,
      clientSecret: formData.clientSecret,
      signingKeyId: formData.signingKeyId,
      signingKey: formData.signingKey,
      paymeTransactionFeePercentage: formData.paymeTransactionFeePercentage,
    }).then((res) => {
      this.setState({ isSaving: false });
      if (res.status === 200) {
        this.setState({ isEditMode: false });
        // Refresh organization data without full page reload
        if (func.loadOrganizationData) {
          func.loadOrganizationData();
        }
      }
    });
  }

  mapFormDataFromProps() {
    const { state } = this.props;
    const orgObj = state.organizationObj || {};

    return {
      clientId: orgObj.clientId || '',
      clientSecret: orgObj.clientSecret || '',
      signingKeyId: orgObj.signingKeyId || '',
      signingKey: orgObj.signingKey || '',
      paymeTransactionFeePercentage: orgObj.paymeTransactionFeePercentage || '',
    };
  }

  maskValue(value, fullyMask = false) {
    if (!value || value === '') return '-';

    if (fullyMask) {
      return '••••••••';
    }

    // Show first 4 and last 4 characters
    if (value.length <= 8) {
      return '••••••••';
    }

    const first = value.substring(0, 4);
    const last = value.substring(value.length - 4);
    return `${first}...${last}`;
  }

  getDisplayValue(field) {
    const { state } = this.props;
    const orgObj = state.organizationObj || {};
    const value = orgObj[field] || '';

    if (field === 'clientSecret' || field === 'signingKey') {
      return this.maskValue(value, true);
    }

    if (field === 'paymeTransactionFeePercentage') {
      return value ? `${value}%` : '1.5% (default)';
    }

    return this.maskValue(value, false);
  }

  render() {
    const { state, func, languageObj } = this.props;
    const { isEditMode, formData, isSaving } = this.state;

    const textObj = languageObj || {};

    if (state.loading || isSaving) {
      return (
        <div className="OrgPayMe-emptyPage">
          <div className="OrgPayMe-center">
            <div className="OrgPayMe-btm-load"></div>
          </div>
        </div>
      );
    }

    const currentPlan = state.organizationObj?.currentPlan
      ? state.organizationObj.currentPlan.toLowerCase()
      : "";

    const isPremiumOrPro = currentPlan === "premium" || currentPlan === "pro";

    if (!isPremiumOrPro) {
      const upgradeBody = "Please upgrade to Pro or above to start accepting PayMe as a supported payment method.";
      return (
        <div className="OrgPayMe-contentContainer">
          <div className="OrgPayMe-contentHeader">
            <div className="OrgPayMe-info">{textObj.title || "PayMe for Business"}</div>
          </div>
          <div className="OrgPayMe-content">
            <UpgradeNoticeCard
              heading={textObj.upgradeHeading}
              body={upgradeBody}
              buttonLabel={textObj.upgradeBtn}
              onUpgradeClick={func.redirectToOrgBilling}
            />
          </div>
        </div>
      );
    }

    return (
      <div className="OrgPayMe-contentContainer">
        <div className="OrgPayMe-contentHeader">
          <div className="OrgPayMe-info">{textObj.title || "PayMe for Business"}</div>

          <div onClick={this.handleToggleEditMode}>
            {isEditMode ? (
              <div className="OrgPayMe-cancelBtn">Cancel</div>
            ) : (
              <div className="OrgPayMe-editBtn">Edit</div>
            )}
          </div>

          {isEditMode && (
            <div className="OrgPayMe-submitBtn" onClick={this.handleSave}>
              Save
            </div>
          )}
        </div>

        <div className="OrgPayMe-content">
          <div className="OrgPayMe-paymelogo"></div>

          <div className="OrgPayMe-savedCredentials">
            <div className="OrgPayMe-group">
              <div className="OrgPayMe-subheading">Client ID</div>
              {isEditMode ? (
                <div className="OrgPayMe-inputFieldContainer">
                  <input
                    type="text"
                    name="clientId"
                    className="OrgPayMe-inputField"
                    placeholder="Client ID"
                    value={formData.clientId}
                    onChange={this.handleInputChange}
                  />
                </div>
              ) : (
                <div className="OrgPayMe-textValue">
                  {this.getDisplayValue('clientId')}
                </div>
              )}
            </div>

            <div className="OrgPayMe-group">
              <div className="OrgPayMe-subheading">Client Secret</div>
              {isEditMode ? (
                <div className="OrgPayMe-inputFieldContainer">
                  <input
                    type="text"
                    name="clientSecret"
                    className="OrgPayMe-inputField"
                    placeholder="Client Secret"
                    value={formData.clientSecret}
                    onChange={this.handleInputChange}
                  />
                </div>
              ) : (
                <div className="OrgPayMe-textValue">
                  {this.getDisplayValue('clientSecret')}
                </div>
              )}
            </div>

            <div className="OrgPayMe-group">
              <div className="OrgPayMe-subheading">Signing Key ID</div>
              {isEditMode ? (
                <div className="OrgPayMe-inputFieldContainer">
                  <input
                    type="text"
                    name="signingKeyId"
                    className="OrgPayMe-inputField"
                    placeholder="Signing Key ID"
                    value={formData.signingKeyId}
                    onChange={this.handleInputChange}
                  />
                </div>
              ) : (
                <div className="OrgPayMe-textValue">
                  {this.getDisplayValue('signingKeyId')}
                </div>
              )}
            </div>

            <div className="OrgPayMe-group">
              <div className="OrgPayMe-subheading">Signing Key</div>
              {isEditMode ? (
                <div className="OrgPayMe-inputFieldContainer">
                  <input
                    type="text"
                    name="signingKey"
                    className="OrgPayMe-inputField"
                    placeholder="Signing Key"
                    value={formData.signingKey}
                    onChange={this.handleInputChange}
                  />
                </div>
              ) : (
                <div className="OrgPayMe-textValue">
                  {this.getDisplayValue('signingKey')}
                </div>
              )}
            </div>

            <div className="OrgPayMe-group">
              <div className="OrgPayMe-subheading">Transaction Fee Percentage</div>
              {isEditMode ? (
                <>
                  <div className="OrgPayMe-inputFieldContainer OrgPayMe-feeInputContainer">
                    <input
                      type="number"
                      name="paymeTransactionFeePercentage"
                      className="OrgPayMe-inputField OrgPayMe-feeInput"
                      placeholder="1.5"
                      step="0.01"
                      min="0"
                      max="100"
                      value={formData.paymeTransactionFeePercentage}
                      onChange={this.handleInputChange}
                    />
                    <span className="OrgPayMe-feePercent">%</span>
                  </div>
                  <div className="OrgPayMe-feeHelp">
                    This fee is used to calculate cover fees when donors choose to cover transaction costs. Default: 1.5%
                  </div>
                </>
              ) : (
                <div className="OrgPayMe-textValue">
                  {this.getDisplayValue('paymeTransactionFeePercentage')}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }
}

RenderPayMe.propTypes = {
  state: PropTypes.object.isRequired,
  func: PropTypes.object.isRequired,
  languageObj: PropTypes.object,
};

export default RenderPayMe;
